import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useLivePrice } from '@/api/digital-gold/livePrice';
import routes from '@/routes/routes.json';
import useMoengageStore from '@/store/moengage';
import { createMoengageEvent } from '@/utils/moengage';

import { DgNewEximNote } from './DgNewEximNote';
import { DgPoweredBy } from './DgPoweredBy';

type SellForm = {
  activeSellTab: string;
  sellInRupees: string;
  sellInGrams: string;
};

type BuyForm = {
  activeType: string;
  buyInput: string;
  buyInputInGrams: string;
};

export const Caluclator = () => {
  const router = useRouter();
  const { livePriceData } = useLivePrice();
  const liveSellAmount: number = livePriceData.saleGoldRate;
  const liveBuyAmount: number = livePriceData.buyGoldRate;

  const buySchema: any = yup.object({
    activeType: yup.string(),
    buyInput: yup.string().when('activeType', {
      is: (activeType: string) => activeType === 'inRupees',
      then: (x) =>
        x
          .required('*Required')
          .test('buyInput', 'Minimum ₹1', (value) => parseFloat(value) >= 1)
          .test(
            'buyInput',
            'Maximum ₹100000',
            (value) => parseFloat(value) <= 100000
          ),
      otherwise: (x) => x.notRequired(),
    }),
    buyInputInGrams: yup.string().when('activeType', {
      is: (activeType: string) => activeType === 'inGrams',
      then: (x) =>
        x
          .required('*Required')
          .test(
            'buyInputInGrams',
            'Minimum ₹1',
            (value) => parseFloat(value) * liveBuyAmount >= 1
          )
          .test(
            'buyInputInGrams',
            'Maximum ₹100000',
            (value) => parseFloat(value) * liveBuyAmount <= 100000
          ),
      otherwise: (x) => x.notRequired(),
    }),
  });

  const sellSchema: any = yup.object({
    activeSellTab: yup.string(),
    sellInRupees: yup.string().when('activeSellTab', {
      is: (activeSellTab: string) => activeSellTab === 'sellInRupees',
      then: (x) =>
        x
          .required('*Required')
          .test(
            'sellInRupees',
            'Minimum ₹50',
            (value) => parseFloat(value) >= 50
          ),
      otherwise: (x) => x.notRequired(),
    }),
    sellInGrams: yup.string().when('activeSellTab', {
      is: (activeSellTab: string) => activeSellTab === 'sellInGrams',
      then: (x) =>
        x
          .required('*Required')
          .test(
            'sellInGrams',
            'Minimum ₹50',
            (value) => parseFloat(value) * liveSellAmount >= 50
          ),
      otherwise: (x) => x.notRequired(),
    }),
  });

  const {
    register,
    setValue,
    watch,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<BuyForm>({
    defaultValues: {
      activeType: 'inRupees',
      buyInput: '',
      buyInputInGrams: '',
    },
    resolver: yupResolver(buySchema),
  });

  const {
    register: sellRegister,
    watch: sellWatch,
    setValue: sellSetValues,
    getValues: getSellValues,
    formState: { errors: sellErrors },
    handleSubmit: sellTypeSubmit,
  } = useForm<SellForm>({
    defaultValues: {
      activeSellTab: 'sellInRupees',
      sellInRupees: '',
      sellInGrams: '',
    },
    resolver: yupResolver(sellSchema),
  });

  const [dgBuyNowClicked] = useMoengageStore((state) => [
    state.dgBuyNowClicked,
  ]);

  useEffect(() => {
    setValue('buyInput', '');
    setValue('buyInputInGrams', '');
  }, [watch('activeType')]);

  useEffect(() => {
    sellSetValues('sellInRupees', '');
    sellSetValues('sellInGrams', '');
  }, [sellWatch('activeSellTab')]);

  const onSubmit = (_data: BuyForm) => {
    createMoengageEvent(dgBuyNowClicked, {
      created_date: moment().format('MMMM Do YYYY, h:mm:ss a'),
      service_type: 'dg',
      selected_amount: getValues('buyInput'),
      selected_gram: getValues('buyInputInGrams'),
      toggle_selected: getValues('activeType'),
      live_buying_price: livePriceData.buyGoldRate,
    });
    router.push(
      `${routes.buyGoldLanding}?amount=${getValues(
        'buyInput'
      )}&grams=${getValues('buyInputInGrams')}&type=${getValues('activeType')}`
    );
  };

  const onSubmitSell = (_data: SellForm) => {
    router.push(
      `${routes.sellGoldLanding}?amount=${getSellValues(
        'sellInRupees'
      )}&grams=${getSellValues('sellInGrams')}&type=${getSellValues(
        'activeSellTab'
      )}`
    );
  };

  const addAmount = (x: number, clcType: string) => {
    if (clcType === 'Buy') {
      setValue(
        'buyInput',
        (
          parseFloat(watch('buyInput') === '' ? '0' : getValues('buyInput')) + x
        ).toString()
      );
    } else {
      sellSetValues(
        'sellInRupees',
        (
          parseFloat(
            sellWatch('sellInRupees') === ''
              ? '0'
              : getSellValues('sellInRupees')
          ) + x
        ).toString()
      );
    }
  };

  const addGrams = (x: number, clcType: string) => {
    if (clcType === 'Buy') {
      setValue(
        'buyInputInGrams',
        (
          parseFloat(
            watch('buyInputInGrams') === '' ? '0' : getValues('buyInputInGrams')
          ) + x
        ).toString()
      );
    } else {
      sellSetValues(
        'sellInGrams',
        (
          parseFloat(
            sellWatch('sellInGrams') === '' ? '0' : sellWatch('sellInGrams')
          ) + x
        ).toString()
      );
    }
  };

  // eslint-disable-next-line
  const [totalPayableAmount, setTotalPayableAmount] = useState<string>('0.00');

  const activeType = watch('activeType');
  const buyInput = watch('buyInput');
  const buyInputInGrams = watch('buyInputInGrams');

  useEffect(() => {
    if (activeType === 'inRupees') {
      setTotalPayableAmount(buyInput || '0.00');
      return;
    }

    if (!buyInputInGrams) {
      setTotalPayableAmount('0.00');
      return;
    }

    setTotalPayableAmount(
      (parseFloat(watch('buyInputInGrams')) * liveBuyAmount).toFixed(2)
    );
  }, [activeType, buyInput, buyInputInGrams, liveBuyAmount]);

  // TODO : Fallback html code based on DG flow enabling status
  const isDGEnabled = true;

  return (
    <section className="bg bg--light investing ltv-padding">
      <div className="container">
        <div className="row align-items-center">
          <div className="col-lg-5 col-xl-6">
            <h2 className="text--black section__title">
              Start investing with India&apos;s leader in eSwarna Digital Gold
            </h2>
            <div className="investing__icons web-view">
              {/* <div className="item right_item">
                <p className="text--semi text--gray assured-text">POWERED BY</p>
                <Image
                  style={{ color: undefined }}
                  className=" hero_assured img-fluid"
                  src={dgMuthoot}
                  alt="power by muthoot"
                />
              </div>
              <div className="item left_item">
                <p className="text--semi text--gray assured-text">ASSURED</p>
                <Image
                  style={{ color: undefined }}
                  className=" hero_assured img-fluid"
                  src={dgAssured}
                  alt="assured 100% safety"
                />
              </div> */}
              <DgPoweredBy />
            </div>
          </div>
          <div className="col-lg-6 pledge visitSchedule">
            <div className="loanDetails__card loanCard tabCard">
              <nav className="nav nav-pills nav-justified">
                <a
                  className="nav-item nav-link active"
                  id="nav-buy-tab"
                  data-toggle="tab"
                  href="#buy-tab"
                  role="tab"
                  aria-controls="buy-tab"
                  aria-selected="true"
                >
                  Buy
                </a>
                <a
                  className="nav-item nav-link"
                  id="nav-sell-tab"
                  data-toggle="tab"
                  href="#sell-tab"
                  role="tab"
                  aria-controls="sell-tab"
                  aria-selected="false"
                >
                  Sell
                </a>
              </nav>
              <div className="tab-content" id="nav-tabContent">
                {/* <BuyCalculator addAmount={(x:number,y:string)=>addAmount(x,y)} /> */}
                <form
                  className="tab-pane fade show active"
                  id="buy-tab"
                  role="tabpanel"
                  aria-labelledby="nav-buy-tab"
                  autoComplete="off"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="loanRequirements">
                    <div className="livePrice">
                      <p className="liveChips text--semi text--white sell_p">
                        <span className="pulsingDot"></span> Live
                      </p>
                      <p className="text--regular text--black title sell_p">
                        Buying Price:{' '}
                        <span className="text--bold">₹{liveBuyAmount}/gm</span>
                      </p>
                    </div>
                    <div className="radio__container">
                      <label
                        className="radio__circle"
                        onClick={() => setValue('activeType', 'inRupees')}
                      >
                        <input
                          type="radio"
                          {...register('activeType')}
                          value="inRupees"
                          id="digital-gold_in-rupees"
                        />
                        <span className="radio__circle-checkmark"></span>
                        <p className="text--regular text--black radio__circle-label">
                          Buy in Rupees
                        </p>
                      </label>
                      <label
                        className="radio__circle"
                        onClick={() => setValue('activeType', 'inGrams')}
                      >
                        <input
                          type="radio"
                          {...register('activeType')}
                          value="inGrams"
                          id="digital-gold_in-grams"
                        />
                        <span className="radio__circle-checkmark"></span>
                        <p className="text--regular text--black radio__circle-label">
                          Buy in Grams
                        </p>
                      </label>
                    </div>
                    {watch('activeType') === 'inRupees' ? (
                      <div className="input-container">
                        <div
                          className={`${
                            errors.buyInput
                              ? 'input_error-border'
                              : 'input-form'
                          } justify-content-between`}
                        >
                          <input
                            className="sell_input"
                            type="number"
                            step={0.01}
                            placeholder="Enter Amount"
                            {...register('buyInput')}
                            id="digital-gold_sell-input"
                          />
                          <span className="text--semi">
                            {' '}
                            ={' '}
                            {watch('buyInput') === ''
                              ? 0.0
                              : (
                                  parseFloat(watch('buyInput')) / liveBuyAmount
                                ).toFixed(4)}{' '}
                            gms
                          </span>
                        </div>
                        {errors.buyInput && (
                          <p className="text--semi errorMessage withInfo showError">
                            {errors.buyInput.message}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="input-container">
                        <div
                          className={`${
                            errors.buyInputInGrams
                              ? 'input_error-border'
                              : 'input-form'
                          } justify-content-between`}
                        >
                          <input
                            type="number"
                            step={0.0001}
                            placeholder="Enter Grams"
                            className="sell_input"
                            {...register('buyInputInGrams')}
                            id="digital-gold_sell-grams"
                          />
                          <span className="text--semi">
                            {' '}
                            = ₹{' '}
                            {watch('buyInputInGrams') === ''
                              ? 0.0
                              : (
                                  parseFloat(watch('buyInputInGrams')) *
                                  liveBuyAmount
                                ).toFixed(2)}{' '}
                          </span>
                        </div>
                        {errors.buyInputInGrams && (
                          <p className="text--semi errorMessage withInfo showError">
                            {errors.buyInputInGrams.message}
                          </p>
                        )}
                      </div>
                    )}
                    {watch('activeType') === 'inRupees' ? (
                      <div className="chips--container">
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addAmount(100, 'Buy')}
                          id="digital-gold_100"
                        >
                          + ₹100
                        </button>
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addAmount(500, 'Buy')}
                          id="digital-gold_500"
                        >
                          + ₹500
                        </button>
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addAmount(1000, 'Buy')}
                          id="digital-gold_1000"
                        >
                          + ₹1,000
                        </button>
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addAmount(5000, 'Buy')}
                          id="digital-gold_5000"
                        >
                          + ₹5,000
                        </button>
                      </div>
                    ) : (
                      <div className="chips--container">
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addGrams(0.25, 'Buy')}
                          id="digital-gold_gms-25"
                        >
                          + 0.25gms
                        </button>
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addGrams(0.5, 'Buy')}
                          id="digital-gold_gms-5"
                        >
                          + 0.5gms
                        </button>
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addGrams(1, 'Buy')}
                          id="digital-gold_gms-1"
                        >
                          + 1.0gm
                        </button>
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addGrams(2, 'Buy')}
                          id="digital-gold_gms-2"
                        >
                          + 2.0gms
                        </button>
                      </div>
                    )}
                    <div className="action-container">
                      {isDGEnabled && (
                        <div className="left max-w-65">
                          <p className="action-label text--semi text--gray">
                            Amount Payable
                          </p>
                          <p className="text--bold text--black action-title ">
                            ₹ {totalPayableAmount}{' '}
                            <span className="text--semi">(incl. 3% GST)</span>
                          </p>
                        </div>
                      )}

                      {isDGEnabled ? (
                        <button
                          type="submit"
                          className="custom__button custom__button--dark text--bold"
                          id="digital-gold_buy-now"
                        >
                          Buy Now
                        </button>
                      ) : (
                        <a
                          href="https://qrco.de/mfone"
                          target="_blank"
                          className="custom__button custom__button--dark text--bold download_sell"
                          // style={{
                          //   margin: 'auto',
                          //   maxWidth: 'fit-content',
                          // }}
                          id="digital-gold_download"
                        >
                          Download App to Buy Gold
                        </a>
                      )}
                    </div>
                    {isDGEnabled && <DgNewEximNote />}
                  </div>
                </form>
                <form
                  className="tab-pane fade"
                  id="sell-tab"
                  role="tabpanel"
                  aria-labelledby="nav-sell-tab"
                  autoComplete="off"
                  onSubmit={sellTypeSubmit(onSubmitSell)}
                >
                  <div className="loanRequirements">
                    <div className="livePrice">
                      <p className="liveChips text--semi text--white sell_p">
                        <span className="pulsingDot"></span> Live
                      </p>
                      <p className="text--regular text--black title sell_p">
                        Selling Price:{' '}
                        <span className="text--bold">₹{liveSellAmount}/gm</span>
                      </p>
                    </div>
                    <div className="radio__container">
                      <label
                        className="radio__circle"
                        onClick={() =>
                          sellSetValues('activeSellTab', 'sellInRupees')
                        }
                      >
                        <input
                          type="radio"
                          {...sellRegister('activeSellTab')}
                          value="sellInRupees"
                          id="digital-gold_sell-rupees-radio"
                        />
                        <span className="radio__circle-checkmark"></span>
                        <p className="text--regular text--black radio__circle-label">
                          Sell in Rupees
                        </p>
                      </label>
                      <label
                        className="radio__circle"
                        onClick={() =>
                          sellSetValues('activeSellTab', 'sellInGrams')
                        }
                      >
                        <input
                          type="radio"
                          {...sellRegister('activeSellTab')}
                          value="sellInGrams"
                          id="digital-gold_sell-gms-radio"
                        />
                        <span className="radio__circle-checkmark"></span>
                        <p className="text--regular text--black radio__circle-label">
                          Sell in Grams
                        </p>
                      </label>
                    </div>
                    {sellWatch('activeSellTab') === 'sellInRupees' ? (
                      <div className="input-container">
                        <div
                          className={`${
                            sellErrors.sellInRupees
                              ? 'input_error-border'
                              : 'input-form'
                          } justify-content-between`}
                        >
                          <input
                            type="number"
                            step={0.01}
                            className="sell_input"
                            placeholder="Enter Amount"
                            {...sellRegister('sellInRupees')}
                            id="digital-gold_sell-rupees"
                          />
                          <span className="text--semi">
                            {' '}
                            ={' '}
                            {sellWatch('sellInRupees') === ''
                              ? 0.0
                              : (
                                  parseFloat(sellWatch('sellInRupees')) /
                                  liveSellAmount
                                ).toFixed(4)}{' '}
                            gms
                          </span>
                        </div>
                        {sellErrors.sellInRupees && (
                          <p className="text--semi errorMessage withInfo showError">
                            {sellErrors.sellInRupees?.message}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="input-container">
                        <div
                          className={`${
                            sellErrors.sellInGrams
                              ? 'input_error-border'
                              : 'input-form'
                          } justify-content-between`}
                        >
                          <input
                            type="number"
                            step={0.0001}
                            className="sell_input"
                            placeholder="Enter Grams"
                            {...sellRegister('sellInGrams')}
                            id="digital-gold_sell-gms"
                          />
                          <span className="text--semi">
                            {' '}
                            = ₹{' '}
                            {sellWatch('sellInGrams') === ''
                              ? 0.0
                              : (
                                  parseFloat(sellWatch('sellInGrams')) *
                                  liveSellAmount
                                ).toFixed(2)}{' '}
                          </span>
                        </div>
                        {sellErrors.sellInGrams && (
                          <p className="text--semi errorMessage withInfo showError">
                            {sellErrors.sellInGrams?.message}
                          </p>
                        )}
                      </div>
                    )}
                    {sellWatch('activeSellTab') === 'sellInRupees' ? (
                      <div className="chips--container">
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addAmount(100, 'Sell')}
                          id="digital-gold_sell-100"
                        >
                          + ₹100
                        </button>
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addAmount(500, 'Sell')}
                          id="digital-gold_sell-500"
                        >
                          + ₹500
                        </button>
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addAmount(1000, 'Sell')}
                          id="digital-gold_sell-1000"
                        >
                          + ₹1,000
                        </button>
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addAmount(5000, 'Sell')}
                          id="digital-gold_sell-5000"
                        >
                          + ₹5,000
                        </button>
                      </div>
                    ) : (
                      <div className="chips--container">
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addGrams(0.25, 'Sell')}
                          id="digital-gold_sell-gms-25"
                        >
                          + 0.25gms
                        </button>
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addGrams(0.5, 'Sell')}
                          id="digital-gold_sell-gms-5"
                        >
                          + 0.5gms
                        </button>
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addGrams(1, 'Sell')}
                          id="digital-gold_sell-gms-1"
                        >
                          + 1.0gm
                        </button>
                        <button
                          type="button"
                          className="chips text--black text--semi"
                          onClick={() => addGrams(2, 'Sell')}
                          id="digital-gold_sell-gms-2"
                        >
                          + 2.0gms
                        </button>
                      </div>
                    )}
                    <div className="action-container">
                      {isDGEnabled ? (
                        <button
                          type="submit"
                          className="custom__button custom__button--dark text--bold"
                          id="digital-gold_sell-now"
                        >
                          Sell Now
                        </button>
                      ) : (
                        <a
                          href="https://qrco.de/mfone"
                          target="_blank"
                          className="custom__button custom__button--dark text--bold download_sell"
                          // style={{
                          //   margin: 'auto',
                          //   maxWidth: 'fit-content',
                          // }}
                          id="digital-gold_sell-download"
                        >
                          Download App to Sell Gold
                        </a>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>
            <div className="investing__icons mobile-view">
              {/*  <div className="item right_item">
                <p className="text--semi text--gray assured-text">POWERED BY</p>
                <Image
                  style={{ color: undefined }}
                  className=" hero_assured img-fluid"
                  src={dgMuthoot}
                  alt="power by muthoot"
                />
              </div>
              <div className="item left_item">
                <p className="text--semi text--gray assured-text">ASSURED</p>
                <Image
                  style={{ color: undefined }}
                  className=" hero_assured img-fluid"
                  src={dgAssured}
                  alt="assured 100% safety"
                />
              </div> */}
              <DgPoweredBy />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
